// PopUpNotificacionWeb.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./PopUpNotificacionWeb.css";

const API_URL = process.env.REACT_APP_API_POPUP_ACTUAL;

const getTipo = (w) => {
    if (w >= 992) return "PC";
    if (w >= 768) return "TABLET";
    return "CEL";
};

const hoyLocal = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD

export default function PopUpNotificacionWeb() {
    const [visible, setVisible] = useState(false);
    const [img, setImg] = useState(null);

    // lista completa del back (TODAS las del día)
    const [listaAll, setListaAll] = useState([]);

    // lista del tipo actual (PC/TABLET/CEL) sin refetch
    const [lista, setLista] = useState([]);

    const [loadingImg, setLoadingImg] = useState(false);
    const [tipo, setTipo] = useState(() => getTipo(window.innerWidth));

    const hoy = useMemo(() => hoyLocal(), []);

    // ✅ guardo el popup actual (no depende de lista/index que cambia con resize)
    const currentRef = useRef(null); // { id, idx, tipo }

    // ✅ clave por día + id (no por tipo)
    const storageKey = (id) => `popupWebMostrado_${hoy}_${id}`;

    const buscarSiguiente = (arr, startIndex) => {
        for (let i = startIndex; i < arr.length; i++) {
            const item = arr[i];
            if (!item?.id) continue;
            if (localStorage.getItem(storageKey(item.id))) continue; // ya visto hoy
            return i;
        }
        return -1;
    };

    const mostrarEn = (arr, idx) => {
        const item = arr[idx];
        if (!item) return;

        setLoadingImg(true);

        const pre = new window.Image();
        pre.src = item.imagenBase64;

        pre.onload = () => {
            currentRef.current = { id: item.id, idx, tipo: (item.tipo || "").toUpperCase() };
            setImg(item.imagenBase64);
            setVisible(true);
            setLoadingImg(false);
        };

        pre.onerror = () => {
            setLoadingImg(false);
            const next = buscarSiguiente(arr, idx + 1);
            if (next !== -1) mostrarEn(arr, next);
            else {
                setVisible(false);
                setImg(null);
                currentRef.current = null;
            }
        };
    };

    const cerrar = () => {
        const current = currentRef.current;

        // ✅ marco como visto el que realmente estaba viendo (aunque cambie la resolución)
        if (current?.id) localStorage.setItem(storageKey(current.id), "1");

        setVisible(false);
        setImg(null);

        // ✅ si cambió el tipo mientras estaba abierto, arrancamos desde 0 en la lista actual
        const startFrom =
            current && current.tipo === tipo ? current.idx + 1 : 0;

        const next = buscarSiguiente(lista, startFrom);
        if (next !== -1) {
            setTimeout(() => mostrarEn(lista, next), 120);
        } else {
            currentRef.current = null;
        }
    };

    // bloquear scroll cuando el modal está visible
    useEffect(() => {
        if (!visible) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [visible]);

    // ✅ 1 sola consulta al back (trae TODO lo activo hoy)
    useEffect(() => {
        const controller = new AbortController();

        const cargar = async () => {
            try {
                const res = await fetch(API_URL, { signal: controller.signal });
                if (!res.ok) return;

                const data = await res.json(); // array
                if (!Array.isArray(data) || data.length === 0) return;

                // seguridad por fechas (si el back ya filtra, igual ok)
                const hoyDate = new Date(hoy);
                const activos = data.filter((p) => {
                    const inicio = new Date(p.fechaInicio);
                    const fin = new Date(p.fechaFin);
                    return hoyDate >= inicio && hoyDate <= fin;
                });

                if (activos.length === 0) return;

                setListaAll(activos);
            } catch (e) {
                if (e.name !== "AbortError") console.error("Popup web error:", e);
            }
        };

        cargar();
        return () => controller.abort();
    }, [hoy]);

    // ✅ resize: cambia tipo SIN refetch
    useEffect(() => {
        const onResize = () => setTipo(getTipo(window.innerWidth));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // ✅ cuando cambia tipo o llega listaAll: recalculo lista del tipo
    // ⚠️ PERO: si ya hay un popup visible, NO lo piso.
    useEffect(() => {
        if (!listaAll.length) return;

        const filtrados = listaAll.filter(
            (p) => (p.tipo || "").toUpperCase() === tipo
        );

        setLista(filtrados);

        // si no hay nada para este tipo, no hago nada
        if (filtrados.length === 0) return;

        // ✅ si ya está visible, no reinicio (solo cambio para el siguiente cierre)
        if (visible) return;

        const first = buscarSiguiente(filtrados, 0);
        if (first === -1) return;

        mostrarEn(filtrados, first);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipo, listaAll]);

    if (loadingImg) return null;
    if (!visible || !img) return null;

    return (
        <div className="popup-overlay" onClick={cerrar}>
            <div className="popupmodalbody popupmodalbody--web" onClick={(e) => e.stopPropagation()}>
                <button
                    className="popupmodalclosebtn popupmodalclosebtn--web"
                    onClick={(e) => {
                        e.stopPropagation();
                        cerrar();
                    }}
                    aria-label="Cerrar"
                >
                    <svg
                        width="28"
                        height="28"
                        viewBox="0 0 30 30"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.61612 6.61612C7.10427 6.12796 7.89573 6.12796 8.38388 6.61612L15 13.2322L21.6161 6.61612C22.1043 6.12796 22.8957 6.12796 23.3839 6.61612C23.872 7.10427 23.872 7.89573 23.3839 8.38388L16.7678 15L23.3839 21.6161C23.872 22.1043 23.872 22.8957 23.3839 23.3839C22.8957 23.872 22.1043 23.872 21.6161 23.3839L15 16.7678L8.38388 23.3839C7.89573 23.872 7.10427 23.872 6.61612 23.3839C6.12796 22.8957 6.12796 22.1043 6.61612 21.6161L13.2322 15L6.61612 8.38388C6.12796 7.89573 6.12796 7.10427 6.61612 6.61612Z"
                            fill="#111420"
                        />
                    </svg>
                </button>

                <div className="popupmodalinner popupmodalinner--web">
                    <div className="popupmodalimg popupmodalimg--web">
                        <img src={img} alt="Notificación" className="popupmodalimgdesktop" />
                        <img src={img} alt="Notificación" className="popupmodalimgmobile" />
                    </div>
                </div>
            </div>
        </div>
    );
}
